import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Box from '~/common/components/Box';
import Heading from '~/common/components/Heading';
import useLeftDrawerContext from '~/common/components/Nav/LeftDrawer/context/useLeftDrawerContext';
import VStack from '~/common/components/Stacks/VStack';
import { HOTKEY_SHORTCUTS } from '~/common/constants';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import { useProductAndCategories } from '~/common/hooks/useProductAndCategories';
import useUrl from '~/common/hooks/useUrl';
import Portal from '~/common/mui/Portal/Portal';
import { hexToRgba } from '~/common/util/format';
import { CATEGORY_DETAILS } from '~/pages/Home/constants';
import palette from '~/theme/mui/palette';

import {
  dispatchCategoryActions,
  getKeyName,
  handleBoltNavigation,
  handleInfoNavigation,
  navigateOrOpenAlert,
} from './GlobalHotkeys.utils';

// Styled component for shortcut list item with bottom border except last
const ShortcutListItem = styled(Box)<{ $isLast?: boolean }>(({ $isLast }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingLeft: 1,
  paddingRight: 1,
  paddingTop: 1,
  paddingBottom: 1,
  marginLeft: 0,
  borderBottom: $isLast ? 'none' : `1px solid ${palette.gray[200]}`,
  listStyle: 'none',
}));

const StyledUnorderedList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  margin: 0,
  gap: '4px',
  alignItems: 'stretch',
});

// Styled component for the right-aligned label in shortcut list items
const StyledRow = styled(Box)({
  textAlign: 'right',
  flex: 1,
  color: palette.gray[700],
  fontSize: '0.875rem',
});

// Styled component for section headings with border and spacing
const StyledHeading = styled(Heading)({
  textAlign: 'left',
  borderBottom: `2px solid ${palette.black}`,
  paddingBottom: 4,
  marginBottom: 8,
});

export default function GlobalHotkeys() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);
  const { handleLeftDrawerCollapseForParentAlert } = useLeftDrawerContext();
  const { obfuscatedLoanIdentifier } = useUrl();
  const obfuscatedLenderDatabaseId = `${obfuscatedLoanIdentifier?.obfuscatedLenderDatabaseId || ''}`;
  const obfuscatedLoanRecordId = `${obfuscatedLoanIdentifier?.obfuscatedLoanRecordId || ''}`;
  const allCategories = useSelector(
    (state: any) => state.home?.navigationItems?.categories || [],
  );
  const selectedAlert = useSelector((state: any) => state.home?.selectedAlert);

  const productType = useGetLoanSummary().data?.loanInfo?.loanProductType ?? '';

  // Memoize boltItems for performance
  // Define a type for category to avoid 'unknown' property error
  type CategoryType = {
    navigationCategoryId: string;
    navigationGutterItems?: any[];
    // ...other properties as needed
  };
  const boltItems = useMemo(() => {
    const categoryMap = new Map(
      (allCategories as CategoryType[]).map((cat) => [
        cat.navigationCategoryId,
        cat,
      ]),
    );
    return CATEGORY_DETAILS.map(({ name, slug, id }: any) => {
      const category = categoryMap.get(id);
      const alert = category?.navigationGutterItems?.[0];
      return { label: name, slug, alert, categoryId: id };
    });
  }, [allCategories]);

  // Memoize FHA/VA exclusions based on productType for efficient filtering
  const excludedLabels = useMemo(() => {
    const exclusions = new Set<string>();
    if (productType === 'Conventional') {
      exclusions.add('FHA');
      exclusions.add('VA');
    } else if (productType === 'VA') {
      exclusions.add('FHA');
    } else if (productType === 'FHA') {
      exclusions.add('VA');
    }
    return exclusions;
  }, [productType]);

  // Use useProductAndCategories to get filtered infoItems
  const { filteredCategories } = useProductAndCategories();
  // Memoize infoItems and build a Set for fast lookup
  const infoItems = useMemo(() => {
    return filteredCategories.map(({ to, label }) => ({
      label,
      key: to,
      to: `/${obfuscatedLenderDatabaseId}/${obfuscatedLoanRecordId}/summary/${to}`,
    }));
  }, [filteredCategories, obfuscatedLenderDatabaseId, obfuscatedLoanRecordId]);
  const infoKeysSet = useMemo(
    () => new Set(infoItems.map((i) => i.key)),
    [infoItems],
  );

  // Build help items from HOTKEY_SHORTCUTS
  // Memoize action bar shortcuts
  const actionBarShortcuts = useMemo(
    () => [
      { key: 'S', label: 'Continue', type: 'Action Bar' },
      { key: 'A', label: 'Next Document within Classify', type: 'Action Bar' },
      { key: 'D', label: 'Open DocHub/Conditions', type: 'Action Bar' },
      { key: 'F', label: 'Form 1003 Popout', type: 'Action Bar' },
      { key: 'V', label: 'Audit History', type: 'Action Bar' },
      { key: 'T', label: 'Clear to Close Validations', type: 'Action Bar' },
      { key: 'C', label: 'Queue Manager', type: 'Action Bar' },
      { key: 'X', label: 'Fraud Guard', type: 'Action Bar' },
      { key: 'Z', label: 'Automated Underwriting System', type: 'Action Bar' },
    ],
    [],
  );

  // Helper to handle navigation for a shortcut
  const handleShortcutNavigation = (shortcut: any, e: KeyboardEvent) => {
    if (shortcut.type === 'bolt') {
      handleBoltNavigationInternal(shortcut, e);
    } else if (shortcut.type === 'info') {
      handleInfoNavigationInternal(shortcut, e);
    }
  };

  const handleBoltNavigationInternal = (shortcut: any, e: KeyboardEvent) => {
    handleBoltNavigation(
      shortcut,
      e,
      boltItems,
      allCategories,
      dispatchCategoryActionsInternal,
      navigateOrOpenAlertInternal,
    );
  };

  const dispatchCategoryActionsInternal = (categoryId: number) => {
    dispatchCategoryActions(categoryId, dispatch);
  };

  const navigateOrOpenAlertInternal = (item: any) => {
    navigateOrOpenAlert(
      item,
      obfuscatedLenderDatabaseId,
      obfuscatedLoanRecordId,
      navigate,
      handleLeftDrawerCollapseForParentAlert,
      dispatch,
    );
  };

  const handleInfoNavigationInternal = (shortcut: any, e: KeyboardEvent) => {
    handleInfoNavigation(shortcut, e, infoItems, navigate);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if focus is on an input, textarea, or contenteditable
      const target = e.target as HTMLElement;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (e.altKey && e.shiftKey) {
        // ALT+Shift+Enter to show help
        if (e.code === 'Enter' || e.key === 'Enter') {
          e.preventDefault();
          setShowHelp(true);
          return;
        }

        const code = e.code;
        const shortcut =
          HOTKEY_SHORTCUTS[code as keyof typeof HOTKEY_SHORTCUTS];
        if (shortcut) {
          handleShortcutNavigation(shortcut, e);
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Hide help when ALT, Shift, or Enter is released
      if (
        showHelp &&
        (e.key === 'Alt' || e.key === 'Shift' || e.key === 'Enter')
      ) {
        setShowHelp(false);
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    globalThis.addEventListener('keyup', handleKeyUp);
    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown);
      globalThis.removeEventListener('keyup', handleKeyUp);
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [
    boltItems,
    infoItems,
    navigate,
    showHelp,
    dispatch,
    handleLeftDrawerCollapseForParentAlert,
    selectedAlert,
  ]);

  return (
    <>
      {showHelp && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
            aria-label="Keyboard Shortcuts Helper Portal"
          >
            <Box
              sx={{
                bg: palette.white,
                borderRadius: '1.25rem',
                boxShadow: `0 2px 12px ${hexToRgba(palette.black, 0.2)}`,
                p: 6,
                w: '600px',
                maxW: '600px',
                maxHeight: '90vh',
                overflowY: 'auto',
              }}
            >
              <VStack spacing={4} alignItems="stretch">
                <Heading
                  size="md"
                  sx={{
                    color: palette.blue[800],
                    textAlign: 'left',
                    mb: 2,
                  }}
                >
                  Keyboard Shortcuts
                </Heading>
                {/* ACTION BAR SECTION */}
                <Box mb={4}>
                  <StyledHeading size="sm">ACTION BAR</StyledHeading>
                  <StyledUnorderedList as="ul">
                    {actionBarShortcuts.map((item, idx, arr) => {
                      const shortcutLabel = `Alt ${item.key}, ${item.label}`;
                      return (
                        <ShortcutListItem
                          as="li"
                          key={`actionbar-${item.key}`}
                          $isLast={idx === arr.length - 1}
                          aria-label={shortcutLabel}
                        >
                          <Box fontWeight="bold">Alt + {item.key}</Box>
                          <StyledRow>{item.label}</StyledRow>
                        </ShortcutListItem>
                      );
                    })}
                  </StyledUnorderedList>
                </Box>

                {/* BOLT & 1003 SUMMARY SECTION */}
                <Box display="flex" gap={6}>
                  {/* BOLT SECTION */}
                  <Box flex="1">
                    <StyledHeading size="sm">BOLT SECTIONS</StyledHeading>
                    <StyledUnorderedList as="ul">
                      {boltItems
                        .filter((item) => !excludedLabels.has(item.label))
                        .map((item) => {
                          // Find the shortcut key for this item
                          const shortcutEntry = Object.entries(
                            HOTKEY_SHORTCUTS,
                          ).find(
                            ([, shortcut]) =>
                              (shortcut as any).slug === item.slug,
                          );
                          if (!shortcutEntry) return null; // Filter out if no shortcut
                          const [code] = shortcutEntry;
                          return { item, code };
                        })
                        .filter(Boolean)
                        .map(({ item, code }, idx, arr) => {
                          // ...existing code...
                          const isAvailable = allCategories.some(
                            (cat) =>
                              cat.navigationCategoryId === item.categoryId,
                          );
                          const shortcutLabel = `Alt Shift ${getKeyName(code)}, Navigate to ${item.label}${isAvailable ? '' : ' (currently unavailable)'}`;
                          return (
                            <ShortcutListItem
                              as="li"
                              key={`bolt-${item.slug}`}
                              $isLast={idx === arr.length - 1}
                              style={{ opacity: isAvailable ? 1 : 0.5 }}
                              aria-label={shortcutLabel}
                              aria-disabled={!isAvailable}
                            >
                              <Box fontWeight="bold">
                                Alt + Shift + {getKeyName(code)}
                              </Box>
                              <StyledRow>{item.label}</StyledRow>
                            </ShortcutListItem>
                          );
                        })}
                    </StyledUnorderedList>
                  </Box>
                  {/* 1003 SUMMARY SECTION */}
                  <Box flex="1">
                    <StyledHeading size="sm">1003 INFORMATION</StyledHeading>
                    <StyledUnorderedList as="ul">
                      {Object.entries(HOTKEY_SHORTCUTS)
                        .filter(
                          ([, shortcut]) =>
                            shortcut.type === 'info' &&
                            typeof (shortcut as { key?: string }).key ===
                              'string',
                        )
                        .map(([code, shortcut], idx, arr) => {
                          const key = (shortcut as { key: string }).key;
                          const item = infoItems.find(
                            (info) => info.key === key,
                          );
                          const isAvailable = infoKeysSet.has(key);
                          const label = item?.label || key;
                          const shortcutLabel = `Alt Shift ${getKeyName(code)}, Navigate to ${label}${isAvailable ? '' : ' (currently unavailable)'}`;
                          return (
                            <ShortcutListItem
                              as="li"
                              key={`1003-${code}`}
                              $isLast={idx === arr.length - 1}
                              style={{ opacity: isAvailable ? 1 : 0.5 }}
                              aria-label={shortcutLabel}
                              aria-disabled={!isAvailable}
                            >
                              <Box fontWeight="bold">
                                Alt + Shift + {getKeyName(code)}
                              </Box>
                              <StyledRow>{label}</StyledRow>
                            </ShortcutListItem>
                          );
                        })}
                    </StyledUnorderedList>
                  </Box>
                </Box>
                <Box textAlign="center" fontSize="sm" color="gray.600" pt={2}>
                  Release keys (Alt + Shift + Enter) to close
                </Box>
              </VStack>
            </Box>
          </Box>
        </Portal>
      )}
    </>
  );
}
